import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { DashboardLayout } from "@/components/dashboard/DashboardLayout";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { exportData, ExportFormat } from "@/services/exportService";
import { toast } from "sonner";
import { FileDown, FileSpreadsheet, FileText, Download } from "lucide-react";
import { format } from "date-fns";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

type ReportType = "compliance" | "revenue" | "title_ix" | "audit_log" | "ncaa_audit" | "conflict" | "deadlines";

export default function Reports() {
  const [reportType, setReportType] = useState<ReportType>("compliance");
  const [exportFormat, setExportFormat] = useState<ExportFormat>("excel");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");

  const { data: deals } = useQuery({
    queryKey: ["deals_for_report"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("deals" as any)
        .select("*");
      if (error) throw error;
      return data;
    },
  });

  const { data: athletes } = useQuery({
    queryKey: ["athletes_for_report"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("athletes" as any)
        .select("*");
      if (error) throw error;
      return data;
    },
  });

  const { data: auditLogs } = useQuery({
    queryKey: ["audit_logs_for_report"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("audit_logs" as any)
        .select("*")
        .order("created_at", { ascending: false });
      if (error) throw error;
      return data;
    },
  });

  const generateComplianceReport = () => {
    if (!deals) return;

    const filteredDeals = deals.filter((d: any) => {
      if (!startDate || !endDate) return true;
      const dealDate = new Date(d.created_at);
      return dealDate >= new Date(startDate) && dealDate <= new Date(endDate);
    });

    const headers = [
      "Deal ID",
      "Athlete",
      "Brand",
      "Amount",
      "Status",
      "Risk Level",
      "Risk Score",
      "Compliance Status",
      "Submitted Date",
      "Review Date",
    ];

    const rows = filteredDeals.map((deal: any) => [
      deal.id,
      deal.athlete_id,
      deal.brand_name,
      `$${deal.amount.toLocaleString()}`,
      deal.status,
      deal.roc_risk_level || "N/A",
      deal.roc_risk_score || "N/A",
      deal.compliance_status || "N/A",
      format(new Date(deal.created_at), "yyyy-MM-dd"),
      deal.reviewed_at ? format(new Date(deal.reviewed_at), "yyyy-MM-dd") : "N/A",
    ]);

    exportData(
      {
        title: "Compliance_Report",
        headers,
        rows,
      },
      exportFormat
    );

    toast.success(`Compliance report exported as ${exportFormat.toUpperCase()}`);
  };

  const generateRevenueReport = () => {
    if (!deals || !athletes) return;

    const athleteMap = new Map(athletes.map((a: any) => [a.id, a]));

    const filteredDeals = deals.filter((d: any) => {
      if (!startDate || !endDate) return true;
      const dealDate = new Date(d.created_at);
      return dealDate >= new Date(startDate) && dealDate <= new Date(endDate);
    });

    const headers = [
      "Athlete Name",
      "Sport",
      "Total Deals",
      "Total Revenue",
      "Average Deal Value",
      "Active Deals",
    ];

    const athleteRevenue = new Map<string, { 
      name: string;
      sport: string;
      totalDeals: number;
      totalRevenue: number;
      activeDeals: number;
    }>();

    filteredDeals.forEach((deal: any) => {
      const athlete = athleteMap.get(deal.athlete_id);
      if (!athlete) return;

      const existing = athleteRevenue.get(deal.athlete_id) || {
        name: athlete.name,
        sport: athlete.sport,
        totalDeals: 0,
        totalRevenue: 0,
        activeDeals: 0,
      };

      athleteRevenue.set(deal.athlete_id, {
        ...existing,
        totalDeals: existing.totalDeals + 1,
        totalRevenue: existing.totalRevenue + (deal.amount || 0),
        activeDeals: existing.activeDeals + (deal.status === "active" ? 1 : 0),
      });
    });

    const rows = Array.from(athleteRevenue.values()).map((data) => [
      data.name,
      data.sport,
      data.totalDeals.toString(),
      `$${data.totalRevenue.toLocaleString()}`,
      `$${(data.totalRevenue / data.totalDeals).toFixed(2)}`,
      data.activeDeals.toString(),
    ]);

    exportData(
      {
        title: "Revenue_Sharing_Report",
        headers,
        rows,
      },
      exportFormat
    );

    toast.success(`Revenue report exported as ${exportFormat.toUpperCase()}`);
  };

  const generateTitleIXReport = () => {
    if (!deals || !athletes) return;

    const athleteMap = new Map(athletes.map((a: any) => [a.id, a]));

    const filteredDeals = deals.filter((d: any) => {
      if (!startDate || !endDate) return true;
      const dealDate = new Date(d.created_at);
      return dealDate >= new Date(startDate) && dealDate <= new Date(endDate);
    });

    // Group by sport and gender (mock gender data)
    const sportGenderData = new Map<string, { 
      sport: string;
      maleDeals: number;
      femaleDeals: number;
      maleRevenue: number;
      femaleRevenue: number;
    }>();

    filteredDeals.forEach((deal: any) => {
      const athlete = athleteMap.get(deal.athlete_id);
      if (!athlete) return;

      const key = athlete.sport;
      const existing = sportGenderData.get(key) || {
        sport: athlete.sport,
        maleDeals: 0,
        femaleDeals: 0,
        maleRevenue: 0,
        femaleRevenue: 0,
      };

      // Mock gender assignment (in real app, this would come from athlete data)
      const isMale = Math.random() > 0.5;

      sportGenderData.set(key, {
        ...existing,
        maleDeals: existing.maleDeals + (isMale ? 1 : 0),
        femaleDeals: existing.femaleDeals + (isMale ? 0 : 1),
        maleRevenue: existing.maleRevenue + (isMale ? deal.amount : 0),
        femaleRevenue: existing.femaleRevenue + (isMale ? 0 : deal.amount),
      });
    });

    const headers = [
      "Sport",
      "Male Deals",
      "Female Deals",
      "Male Revenue",
      "Female Revenue",
      "Revenue Ratio (M:F)",
    ];

    const rows = Array.from(sportGenderData.values()).map((data) => [
      data.sport,
      data.maleDeals.toString(),
      data.femaleDeals.toString(),
      `$${data.maleRevenue.toLocaleString()}`,
      `$${data.femaleRevenue.toLocaleString()}`,
      data.femaleRevenue > 0 
        ? (data.maleRevenue / data.femaleRevenue).toFixed(2)
        : "N/A",
    ]);

    exportData(
      {
        title: "Title_IX_Report",
        headers,
        rows,
      },
      exportFormat
    );

    toast.success(`Title IX report exported as ${exportFormat.toUpperCase()}`);
  };

  const generateAuditLogReport = () => {
    if (!auditLogs) return;

    const filteredLogs = auditLogs.filter((log: any) => {
      if (!startDate || !endDate) return true;
      const logDate = new Date(log.created_at);
      return logDate >= new Date(startDate) && logDate <= new Date(endDate);
    });

    const headers = [
      "Date",
      "Entity Type",
      "Action",
      "Severity",
      "User",
      "Role",
      "Details",
    ];

    const rows = filteredLogs.map((log: any) => [
      format(new Date(log.created_at), "yyyy-MM-dd HH:mm:ss"),
      log.entity_type,
      log.action,
      log.severity,
      log.user_name || "System",
      log.user_role || "N/A",
      log.details,
    ]);

    exportData(
      {
        title: "Audit_Log_Report",
        headers,
        rows,
      },
      exportFormat
    );

    toast.success(`Audit log exported as ${exportFormat.toUpperCase()}`);
  };

  const generateNCAAAAuditReport = () => {
    if (!deals || !athletes) return;

    const filteredDeals = deals.filter((d: any) => {
      if (!startDate || !endDate) return true;
      const dealDate = new Date(d.created_at);
      return dealDate >= new Date(startDate) && dealDate <= new Date(endDate);
    });

    const headers = [
      "Submission Date",
      "Athlete Name",
      "Sport",
      "Brand/Entity",
      "Deal Category",
      "Compensation Amount",
      "Deal Status",
      "Compliance Review Date",
      "Compliance Officer",
      "Risk Assessment",
      "Title IX Impact",
      "Notes",
    ];

    const rows = filteredDeals.map((deal: any) => [
      format(new Date(deal.created_at), "MM/dd/yyyy"),
      deal.athlete_id,
      "N/A", // Would come from athlete join
      deal.brand_name,
      deal.category || "N/A",
      `$${deal.amount.toLocaleString()}`,
      deal.status.toUpperCase(),
      deal.approved_at ? format(new Date(deal.approved_at), "MM/dd/yyyy") : "Pending",
      deal.approved_by || "N/A",
      deal.risk_level || "Low",
      "Compliant",
      deal.compliance_status || "Under Review",
    ]);

    exportData(
      {
        title: "NCAA_Compliance_Audit_Report",
        headers,
        rows,
      },
      exportFormat
    );

    toast.success(`NCAA Audit Report exported as ${exportFormat.toUpperCase()}`);
  };

  const generateConflictReport = () => {
    if (!deals) return;

    const conflictDeals = deals.filter((d: any) => 
      d.compliance_status === "conflict_detected" || d.risk_level === "high"
    );

    const headers = [
      "Deal ID",
      "Athlete",
      "Brand",
      "Amount",
      "Conflict Type",
      "Risk Level",
      "Status",
      "Flagged Date",
      "Resolution",
    ];

    const rows = conflictDeals.map((deal: any) => [
      deal.id.substring(0, 8),
      deal.athlete_id,
      deal.brand_name,
      `$${deal.amount.toLocaleString()}`,
      deal.compliance_status || "Unknown",
      deal.risk_level || "N/A",
      deal.status,
      format(new Date(deal.created_at), "yyyy-MM-dd"),
      deal.rejection_reason || "Pending Review",
    ]);

    exportData(
      {
        title: "Conflict_Of_Interest_Report",
        headers,
        rows,
      },
      exportFormat
    );

    toast.success(`Conflict report exported as ${exportFormat.toUpperCase()}`);
  };

  const generateDeadlinesReport = () => {
    if (!deals) return;

    const today = new Date();
    const upcomingDeals = deals.filter((d: any) => {
      if (d.status !== "active") return false;
      const endDate = new Date(d.end_date);
      return endDate > today;
    });

    const headers = [
      "Athlete",
      "Brand",
      "Deal Title",
      "Start Date",
      "End Date",
      "Days Remaining",
      "Disclosure Status",
      "Action Required",
    ];

    const rows = upcomingDeals.map((deal: any) => {
      const endDate = new Date(deal.end_date);
      const daysRemaining = Math.ceil((endDate.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
      
      return [
        deal.athlete_id,
        deal.brand_name,
        deal.title,
        format(new Date(deal.start_date), "yyyy-MM-dd"),
        format(endDate, "yyyy-MM-dd"),
        daysRemaining.toString(),
        deal.compliance_status || "Current",
        daysRemaining < 30 ? "Review Required" : "Monitor",
      ];
    });

    exportData(
      {
        title: "Deadline_Tracking_Report",
        headers,
        rows,
      },
      exportFormat
    );

    toast.success(`Deadline report exported as ${exportFormat.toUpperCase()}`);
  };

  const handleGenerateReport = () => {
    switch (reportType) {
      case "compliance":
        generateComplianceReport();
        break;
      case "revenue":
        generateRevenueReport();
        break;
      case "title_ix":
        generateTitleIXReport();
        break;
      case "audit_log":
        generateAuditLogReport();
        break;
      case "ncaa_audit":
        generateNCAAAAuditReport();
        break;
      case "conflict":
        generateConflictReport();
        break;
      case "deadlines":
        generateDeadlinesReport();
        break;
    }
  };

  const reportDescriptions = {
    compliance: "Comprehensive compliance review including risk levels, statuses, and review timelines",
    revenue: "Revenue sharing breakdown by athlete, sport, and deal performance",
    title_ix: "Gender equity analysis across sports programs and deal distributions",
    audit_log: "Complete system activity log with user actions and timestamps",
    ncaa_audit: "NCAA-formatted compliance audit report ready for official submission",
    conflict: "All deals flagged for conflicts of interest or high-risk concerns",
    deadlines: "Upcoming disclosure deadlines and required actions",
  };

  return (
    <DashboardLayout>
      <div className="space-y-6">
        <div>
          <h1 className="text-3xl font-bold">Reports & Export</h1>
          <p className="text-muted-foreground">
            Generate and export compliance reports
          </p>
        </div>

        <Card>
          <CardHeader>
            <CardTitle>Report Configuration</CardTitle>
            <CardDescription>
              {reportDescriptions[reportType]}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="report_type">Report Type</Label>
                <Select value={reportType} onValueChange={(v) => setReportType(v as ReportType)}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="compliance">Compliance Report</SelectItem>
                    <SelectItem value="ncaa_audit">NCAA Audit Report</SelectItem>
                    <SelectItem value="conflict">Conflict of Interest Report</SelectItem>
                    <SelectItem value="deadlines">Deadline Tracking Report</SelectItem>
                    <SelectItem value="revenue">Revenue Sharing Report</SelectItem>
                    <SelectItem value="title_ix">Title IX Report</SelectItem>
                    <SelectItem value="audit_log">Audit Log</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="export_format">Export Format</Label>
                <Select value={exportFormat} onValueChange={(v) => setExportFormat(v as ExportFormat)}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="excel">Excel (.xlsx)</SelectItem>
                    <SelectItem value="csv">CSV (.csv)</SelectItem>
                    <SelectItem value="pdf">PDF (.pdf)</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="start_date">Start Date (Optional)</Label>
                <Input
                  id="start_date"
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="end_date">End Date (Optional)</Label>
                <Input
                  id="end_date"
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                />
              </div>
            </div>

            <Button onClick={handleGenerateReport} className="w-full" size="lg">
              <Download className="mr-2 h-4 w-4" />
              Generate & Download Report
            </Button>
          </CardContent>
        </Card>

        <div className="grid gap-4 md:grid-cols-3">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Excel Export</CardTitle>
              <FileSpreadsheet className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <p className="text-xs text-muted-foreground">
                Full-featured spreadsheet with formatting and formulas
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">CSV Export</CardTitle>
              <FileText className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <p className="text-xs text-muted-foreground">
                Simple data format compatible with all systems
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">PDF Export</CardTitle>
              <FileDown className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <p className="text-xs text-muted-foreground">
                Professional printable format for official records
              </p>
            </CardContent>
          </Card>
        </div>
      </div>
    </DashboardLayout>
  );
}
